// ============================================================================
// SCENE ACCENTS — the clips a room plays OVER its idle
//
// ONE module for BOTH rooms, because there is literally one rig. The 2026-09-02
// export ships `game_view.json` and `bonus_gameview.json` as byte-identical
// files (same skeleton hash, same five clips, same durations) — only the atlas
// page names differ. So a behaviour that is right in the base game is right in
// the bonus room by construction, and duplicating it would only let the two
// drift.
//
// Every function takes the Spine and touches nothing else, so a controller's
// accent methods are one line each. `tag` is only for the warning text.
// ============================================================================

import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

/**
 * Which clip each MOMENT plays — exported so a caller names the moment and
 * never the animation.
 *
 * Candidate lists, like the idle's, so a re-export that renames one does not
 * silently stop playing it.
 */
// ── The two tracks ──
//
// Tracks above 0 are all free in BOTH rooms: the reels, the spine overlay and
// the bonus room's four progress boxes are injected as SLOT OBJECTS, not as
// animations, so nothing else has ever used one. Track 0 is the idle.
//
// MAIN carries whichever accent OWNS the moment — the lamp during a wait, a rat
// reaction during a win. Those never want to be on screen together, so sharing
// one track is the point: the later one replaces the earlier.
//
// REELS is separate because `reel_highlight` plays WITH the lamp rather than
// instead of it, and the two share neither a bone nor a slot.
export const SCENE_ACCENT_TRACKS = { MAIN: 1, REELS: 2 };

export const SCENE_ACCENTS = {
  // The rat gets zapped. The two variants share 23 bones, so they are
  // alternatives and never both — which is why one moment gets each.
  //
  // ── BOTH loop seamlessly, checked bone by bone ──
  //
  // `rat_shocked2` (1000ms) is exact: every bone's first key equals its last.
  //
  // `rat_shocked` (1333ms) looks like it does not — two properties differ
  // between its first and last key — and both turn out to be invisible:
  //
  //   m2_main_bone27.rotate      0 -> -720, which is two whole turns and
  //                              therefore the same pose
  //   electricity_top_rat.scale  0.378 -> 1, on the bone carrying slot
  //                              `m2_energy_01` — whose attachment is REMOVED
  //                              at t=0.5667 and never returns, so the slot is
  //                              empty at the seam and draws nothing
  //
  // So a first-vs-last diff is not the test. What draws at the seam is.
  BIG_WIN: ["rat/rat_shocked2", "rat/rat_shocked"],

  // Every OTHER win, and the bonus trigger. Held the same way — the reaction
  // lasts as long as what it is reacting to, and is released when the board is
  // cleared.
  WIN: ["rat/rat_shocked", "rat/rat_shocked2"],

  // The wait before a mystery opens. 800ms, and neither anticipation is a
  // multiple of it — `my.anticipation` is 500ms and `my.anticipation_strong` is
  // 2000ms — so it is FITTED to the window rather than looped and cut. See
  // playAccentForWindow.
  //
  // Every mystery gets it for the wait, short build-up or long — it says
  // something is being decided, which is equally true of all of them.
  //
  // A Golden Mystery then RE-STARTS it on a loop when the doors show gold and
  // holds it to the end of the feature — see useWinController. The reason the
  // fitting exists does not apply there: nothing is waiting for a window to
  // end, so there is no cut to land on a drawn frame. What it relies on instead
  // is the clip looping cleanly, which has NOT been verified bone by bone the
  // way the rat's was. If the lamp jolts at the seam, that is where to look.
  MYSTERY_WAIT: ["lamp_highlight"],

  // ── The GOLDEN MYSTERY only, from the frame the doors show gold ──
  //
  // 667ms of lightning across the reel frame — 16 `lightning_*` slots.
  //
  // It used to fire on the long BUILD-UP, as a second tier of anticipation the
  // player could tell apart from the lamp. That spent it on a promise the round
  // could not keep: a long build-up reveals an ordinary symbol just as often.
  // It now belongs to the one outcome that earns it, HELD from the reveal to
  // the end of the feature rather than fitted to a window.
  //
  // Same unverified-seam caveat as the lamp above.
  MYSTERY_WAIT_STRONG: ["reel_highlight"],
};

// ── The sound an accent brings with it ──
//
// Keyed on the CLIP, not on the track and not on the call site. Three places
// start the reel electricity — the scatter anticipation in the base game, and
// the Golden Mystery's build-up in each room — and every one of them ends the
// same way, through stopAccent. Hanging the sound off the call sites would be
// three starts and three stops to keep in step; hanging it off the clip is one
// of each, in the module that already owns the clip for both rooms.
//
// Only LOOPING accents qualify. A one-shot lamp flash is over before a loop
// could settle, and it has the sprite for that.
const ACCENT_LOOP_SOUND = {
  reel_highlight: SOUND_NAME.reel_glow,
};

// ── Which loop the scene currently has up ──
//
// A flag, not a count. The accent lives on ONE spine track: `setAnimation`
// replaces whatever was there, so "the glow is on" is a boolean and the sound
// has to mirror exactly that. Reference counting would be wrong — the golden
// mystery starting its glow while a scatter anticipation already has one is
// not two glows, it is the same track being re-set.
//
// Also what makes a second start silent rather than doubled: soundManager.play
// on a Howl that is already looping starts a SECOND playback id and layers it.
let activeAccentSound = null;

const startAccentSound = (name) => {
  if (!name || activeAccentSound === name) return;
  // A different accent taking the track takes the sound with it.
  if (activeAccentSound) soundManager.stop(activeAccentSound);
  activeAccentSound = name;
  soundManager.play(name);
};

const stopAccentSound = () => {
  if (!activeAccentSound) return;
  soundManager.stop(activeAccentSound);
  activeAccentSound = null;
};

// ── Why an accent needs mixing at BOTH ends ──
//
// The rat clips key 20 bones the 6.7s `idle` is also animating. On its own
// track at full alpha the accent overrides those bones and leaves the other 14
// to the idle, which is exactly what is wanted — but the idle does not stop, so
// at both the start and the end the two poses differ by wherever the loop
// happens to be. Without a mix that is a snap in and a snap out.
//
// `reel_highlight` shares nothing with the idle and would not need this; paying
// 120ms for it costs nothing and keeps one code path.
export const SCENE_ACCENT_MIX_MS = 120;

/** First clip of `candidates` the rig actually has, or null. */
function firstAnimation(spine, candidates) {
  const data = spine?.skeleton?.data;
  if (!data) return null;
  for (const name of candidates) {
    if (data.findAnimation(name)) return name;
  }
  return null;
}

/**
 * Play a one-shot accent over the idle, on its own track.
 *
 * The idle is never touched: it keeps looping on track 0 and the accent
 * overrides only the bones it keys, for as long as it runs. When it ends the
 * track is mixed back to empty, so those bones return to the idle rather than
 * snapping to it — see SCENE_ACCENT_MIX_MS.
 *
 * Retriggerable. A second call replaces the first on the same track, which is
 * the right answer for an accent: the newer event is the one worth showing.
 *
 * ── One-shot or HELD ──
 *
 * `loop: false` marks a moment and gets out of the way: it queues its own
 * fade back to the idle, so the caller has nothing to remember.
 *
 * `loop: true` is for a moment that is still on screen — a big win being
 * counted out. It is held until `stopAccent`, and the caller OWNS that call.
 * `rat/rat_shocked2` was authored to loop (checked: every bone's first key
 * equals its last), which is why it is the big-win pick and the 1333ms
 * `rat/rat_shocked` is not — that one ends `electricity_top_rat` at a
 * different scale from where it starts and would jolt every cycle.
 *
 * @param   {string[]} candidates Clip names, best first — see SCENE_ACCENTS.
 * @param   {Object}   [p]
 * @param   {boolean}  [p.loop] Hold it until stopAccent.
 * @returns {boolean} false when the rig has none of them, so a caller can
 *          tell "not played" from "played and not noticed".
 */
export function playSceneAccent(
  spine,
  candidates,
  { loop = false, track = SCENE_ACCENT_TRACKS.MAIN, tag = "Scene" } = {},
) {
  if (!spine?.state) return false;

  const name = firstAnimation(spine, candidates);
  if (!name) {
    console.warn(`[${tag}] No accent animation found:`, candidates);
    return false;
  }

  const mix = SCENE_ACCENT_MIX_MS / 1000;

  const entry = spine.state.setAnimation(track, name, loop);
  if (!entry) return false;
  entry.mixDuration = mix;
  // With the clip, not before it: a rig missing the animation returns above and
  // must not leave a sound playing for something nobody can see.
  if (loop) startAccentSound(ACCENT_LOOP_SOUND[name]);
  if (!loop) {
    // Queued, not timed: Spine works the delay out from the clip's own
    // length, so the fade back to the idle cannot drift from the animation.
    spine.state.addEmptyAnimation(track, mix, 0);
  }
  return true;
}

/**
 * Play an accent so that a whole number of passes EXACTLY fills a window.
 *
 * ── Why not just loop it and cut ──
 *
 * `lamp_highlight` is 800ms and the two mystery anticipations are 500ms and
 * 2000ms. Looped, the short one is cut at 62% of its first pass and the long
 * one halfway through its third — the lamp is mid-flash when the doors open,
 * which is the one frame it must not be.
 *
 * So only the PASS COUNT is chosen, and the clip always runs at the speed it
 * was drawn at:
 *
 *   passes = max(1, ceil(window / clip))
 *
 *   500ms  -> 1 pass  = 800ms,  carries 300ms into the reveal
 *   2000ms -> 3 passes = 2400ms, carries 400ms into it
 *
 * ── The speed is never touched, in either direction ──
 *
 * Two goes at bending it, and both were wrong for the same reason.
 *
 * Speeding UP to fit: one 800ms pass into the 500ms short anticipation needs
 * 1.60x, and at 1.60x the lamp stops reading as a lamp lighting and becomes a
 * flash. Compressing an animation past the speed it was drawn at destroys it.
 *
 * Slowing DOWN to land exactly: 0.80x over two passes filled the 2000ms
 * window to the millisecond and still felt wrong — a lamp flashing at 4/5
 * speed is not the lamp the animator drew either. Exactness was never worth
 * anything here; nothing is counting the milliseconds.
 *
 * `ceil`, so the clip always OUTLASTS the window rather than stopping short
 * of it. A gap before the doors open is dead air; carrying on into the reveal
 * is a handover, and `my.reveal` is 600ms so there is room for it.
 *
 * The pass count is the one thing worth choosing, because it is what the
 * player COUNTS — one flash for a small mystery, three for a big one.
 *
 * @param   {string[]} candidates Clip names, best first — see SCENE_ACCENTS.
 * @param   {number}   windowMs   How long it must fill.
 * @returns {boolean} false when the rig has none of them, or the window is
 *          not a usable length.
 */
export function playSceneAccentForWindow(
  spine,
  candidates,
  windowMs,
  { track = SCENE_ACCENT_TRACKS.MAIN, tag = "Scene" } = {},
) {
  if (!spine?.state || !(windowMs > 0)) return false;

  const name = firstAnimation(spine, candidates);
  if (!name) {
    console.warn(`[${tag}] No accent animation found:`, candidates);
    return false;
  }

  const clipMs =
    (spine.skeleton?.data?.findAnimation?.(name)?.duration ?? 0) * 1000;
  if (!(clipMs > 0)) return false;

  const passes = Math.max(1, Math.ceil(windowMs / clipMs));
  const mix = SCENE_ACCENT_MIX_MS / 1000;

  // Looped, and the fade behind it is what ENDS the loop.
  //
  // `timeScale` is deliberately not set — the clip keeps its authored speed,
  // so whole passes land on a last frame the animator drew.
  const entry = spine.state.setAnimation(track, name, true);
  if (!entry) return false;
  entry.mixDuration = mix;

  // ── The delay is STATED, not inferred ──
  //
  // `addEmptyAnimation(..., 0)` lets Spine work the delay out, and for a
  // LOOPING entry it uses the next loop boundary — one pass. So a three-pass
  // lamp was cut after the first and the long anticipation looked no
  // different from the short one. Leaving the delay to Spine is right for a
  // ONE-SHOT and wrong here, which is the whole difference between the two
  // methods.
  //
  // One mix before the total, so the fade finishes exactly on it.
  const totalMs = passes * clipMs;
  spine.state.addEmptyAnimation(
    track,
    mix,
    Math.max(0, (totalMs - SCENE_ACCENT_MIX_MS) / 1000),
  );
  return true;
}

/**
 * Let a held accent GO — but let it finish first.
 *
 * ── Not a cut ──
 *
 * Stopping a loop where it stands is a cut, and a rat frozen mid-zap reads as a
 * fault rather than an ending. So the pass that is already running plays out to
 * its own 0 frame, and the fade to the idle lands exactly on it.
 *
 * ── `loop` MUST still be true when the fade is queued ──
 *
 * This is the whole of it, and getting it backwards cut the rat on every single
 * call. `addEmptyAnimation(track, mix, 0)` asks the runtime to work the delay
 * out, and the runtime asks the entry (AnimationState.js, getTrackComplete):
 *
 *   if (this.loop) return duration * (1 + ((this.trackTime / duration) | 0));
 *   if (this.trackTime < duration) return duration;
 *   return this.trackTime;  // Next update.
 *
 * LOOPING, it answers with the next loop boundary — the 0 frame that is coming,
 * which is exactly the moment wanted. NOT looping, and `trackTime` is already
 * past `duration` after the first pass, so it falls through to the last line
 * and answers "now": the fade is queued in the past and takes over on the next
 * update. The old code cleared `loop` FIRST and then asked, so the answer was
 * always "now" — which is what was being watched on screen.
 *
 * So the repeat is left alone. Nothing needs to turn it off: the queued entry
 * takes the track at the boundary and the accent's entry is dropped with it.
 *
 * The delay is left to the runtime on purpose, and only here. It is the one
 * caller that cannot know the number — the pass in flight is however far along
 * the win happened to leave it — while playAccentForWindow starts from zero and
 * must state its own. That is the whole difference between the two.
 *
 * Mixed rather than cleared, for the same reason as everywhere else: the accent
 * and the idle both animate those 20 rat bones, so clearing the track would
 * snap them.
 *
 * Safe to call at any time. Nothing playing is a no-op, and so is a second call
 * for the same accent — `next` is the flag that says a fade is already waiting.
 */
export function stopSceneAccents(spine) {
  // FIRST, and unconditionally. This function's whole meaning is "the board is
  // going, so nothing the scene put up may outlive it" — and that has to hold
  // even when the spine is already gone, or a torn-down room would leave the
  // electricity humming over the next one.
  stopAccentSound();

  if (!spine?.state) return;

  // EVERY accent track. A clear means the board is going, so nothing the
  // scene put up for it may outlive it.
  for (const track of Object.values(SCENE_ACCENT_TRACKS)) {
    const current = spine.state.getCurrent(track);
    if (!current) continue;
    // A one-shot, or an accent already mixing out. Both end on their own.
    if (!current.loop) continue;
    // A fade is already waiting — a fitted accent counting out its passes, or a
    // second stop for this same one. Queuing another would move the ending
    // earlier, which is the fault this function exists to avoid.
    if (current.next) continue;

    // Queued while `loop` is STILL true, so the runtime aims at the next 0
    // frame. `current.loop = false` above this line is the bug — see the note.
    spine.state.addEmptyAnimation(track, SCENE_ACCENT_MIX_MS / 1000, 0);
  }
}

